import {
  PayeeRequirement,
  RequirementType
} from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";

import { ExtendedRequirementTableItemData } from "../../../types";
import {
  getIsGeneralRequirement,
  sortRequirementsByPriority
} from "../../../utils";

import { useGetPayoutMethodCustomRequirement } from "./useGetPayoutMethodCustomRequirement";
import { useGetRegistrationCustomRequirement } from "./useGetRegistrationCustomRequirement";
import { useGetTaxVerificationCustomRequirement } from "./useGetTaxVerificationCustomRequirement";

type GeneralRequirements = Partial<Record<RequirementType, PayeeRequirement[]>>;

export function usePayerRequirements(requirements: PayeeRequirement[]) {
  const getTaxVerificationCustomRequirement =
    useGetTaxVerificationCustomRequirement();

  const getPayoutMethodCustomRequirement =
    useGetPayoutMethodCustomRequirement();

  const getRegistrationCustomRequirement =
    useGetRegistrationCustomRequirement();

  const clientsRequirements = useMemo(() => {
    // Filter and SORT non-general requirements
    const normalizedList: ExtendedRequirementTableItemData[] =
      sortRequirementsByPriority(
        requirements.filter(
          requirement => !getIsGeneralRequirement(requirement)
        )
      );

    // Group up general requirements
    const generalRequirements = groupGeneralRequirements(requirements);

    // Build custom requirements in REVERSE order (since unshift adds to front)
    const customRequirements: ExtendedRequirementTableItemData[] = [];

    // Process in reverse order: PayoutMethod, TaxVerification, Registration
    // So that Registration ends up first after all unshifts
    if (generalRequirements[RequirementType.PAYOUT_METHOD]) {
      customRequirements.unshift(
        getPayoutMethodCustomRequirement(
          generalRequirements[RequirementType.PAYOUT_METHOD]!
        )
      );
    }
    if (generalRequirements[RequirementType.TAX_VERIFICATION]) {
      customRequirements.unshift(
        getTaxVerificationCustomRequirement(
          generalRequirements[RequirementType.TAX_VERIFICATION]!
        )
      );
    }
    if (generalRequirements[RequirementType.REGISTRATION]) {
      customRequirements.unshift(
        getRegistrationCustomRequirement(
          generalRequirements[RequirementType.REGISTRATION]!
        )
      );
    }

    // Combine: custom requirements first, then sorted normal requirements
    return [...customRequirements, ...normalizedList];
  }, [
    requirements,
    getTaxVerificationCustomRequirement,
    getPayoutMethodCustomRequirement,
    getRegistrationCustomRequirement
  ]);

  return clientsRequirements;
}

function groupGeneralRequirements(requirements: PayeeRequirement[]) {
  const generalRequirementMap: GeneralRequirements = {};

  requirements.forEach(requirement => {
    if (!getIsGeneralRequirement(requirement)) {
      return;
    }

    const requirementType = requirement.type;
    if (!requirementType) {
      return;
    }

    if (!generalRequirementMap[requirementType]) {
      generalRequirementMap[requirementType] = [];
    }
    generalRequirementMap[requirementType]!.push(requirement);
  });

  return generalRequirementMap;
}
